/**
 * Escrow Service
 *
 * Creates and manages data escrows on the DataEscrow contract.
 * Uses the user's secp256k1 keypair to sign transactions.
 */

import { Contract, parseEther, keccak256, concat, getBytes, hexlify, randomBytes } from 'ethers';
import { getKeypairSigner, DEFAULT_ESCROW_CHAIN_ID } from '../wallet/keypair-wallet';
import { logger } from '../../utils/logger';

// DataEscrow contract on Sepolia
const ESCROW_CONTRACT_ADDRESS = '0xfD9A47e3466576671C1d36f9a347cd27ea24d979';

// Minimal ABI for createEscrow
const ESCROW_ABI = [
  {
    name: 'createEscrow',
    type: 'function',
    stateMutability: 'nonpayable',
    inputs: [
      { name: 'contentHash', type: 'bytes32' },
      { name: 'keyCommitment', type: 'bytes32' },
      { name: 'paymentToken', type: 'address' },
      { name: 'amount', type: 'uint256' },
      { name: 'expirationDays', type: 'uint256' },
    ],
    outputs: [{ name: 'escrowId', type: 'uint256' }],
  },
  {
    name: 'EscrowCreated',
    type: 'event',
    inputs: [
      { name: 'escrowId', type: 'uint256', indexed: true },
      { name: 'seller', type: 'address', indexed: true },
      { name: 'contentHash', type: 'bytes32', indexed: false },
      { name: 'keyCommitment', type: 'bytes32', indexed: false },
      { name: 'amount', type: 'uint256', indexed: false },
      { name: 'expiresAt', type: 'uint256', indexed: false },
    ],
  },
];

export interface CreateEscrowParams {
  /** Swarm reference (64 hex chars) of the encrypted content */
  swarmReference: string;
  /** AES encryption key used for the file (32 bytes) */
  encryptionKey: Uint8Array;
  /** Price in ETH (e.g., "0.01") */
  priceEth: string;
  /** Expiry in days */
  expiryDays: number;
  /** Seller's private key (hex) */
  sellerPrivateKey: string;
}

export interface CreateEscrowResult {
  escrowId: string;
  txHash: string;
  keyCommitment: string;
  salt: Uint8Array;
}

/**
 * Create an escrow for encrypted data
 *
 * @param params - Escrow creation parameters
 * @returns Escrow ID and transaction details
 */
export async function createEscrow(params: CreateEscrowParams): Promise<CreateEscrowResult> {
  const { swarmReference, encryptionKey, priceEth, expiryDays, sellerPrivateKey } = params;

  // Get signer
  const signer = getKeypairSigner(sellerPrivateKey, DEFAULT_ESCROW_CHAIN_ID);

  // Create content hash from Swarm reference (pad to 32 bytes)
  const contentHash = `0x${swarmReference.padStart(64, '0')}`;

  // Create key commitment: keccak256(key || salt)
  const salt = randomBytes(32);
  const keyCommitmentInput = concat([encryptionKey, salt]);
  const keyCommitment = keccak256(keyCommitmentInput);

  // Convert price to wei
  const amountWei = parseEther(priceEth);

  // Create contract instance
  const contract = new Contract(ESCROW_CONTRACT_ADDRESS, ESCROW_ABI, signer);

  // Zero address for ETH payment (not ERC20)
  const zeroAddress = '0x0000000000000000000000000000000000000000';

  // Call createEscrow
  logger.debug('[Escrow] Creating escrow...', {
    contentHash,
    keyCommitment,
    amount: amountWei.toString(),
    expiryDays,
  });

  const tx = await contract.createEscrow(
    contentHash,
    keyCommitment,
    zeroAddress,
    amountWei,
    BigInt(expiryDays)
  );

  logger.debug('[Escrow] Transaction sent:', tx.hash);

  // Wait for confirmation
  const receipt = await tx.wait();
  logger.debug('[Escrow] Transaction confirmed:', receipt.hash);

  // Parse escrow ID from event
  let escrowId = '0';
  for (const log of receipt.logs) {
    try {
      const parsed = contract.interface.parseLog({
        topics: log.topics as string[],
        data: log.data,
      });
      if (parsed?.name === 'EscrowCreated') {
        escrowId = parsed.args.escrowId.toString();
        logger.debug('[Escrow] Escrow created with ID:', escrowId);
        break;
      }
    } catch {
      // Not the event we're looking for
    }
  }

  return {
    escrowId,
    txHash: receipt.hash,
    keyCommitment,
    salt: getBytes(hexlify(salt)),
  };
}

/**
 * Generate a random 32-byte encryption key
 */
export function generateEncryptionKey(): Uint8Array {
  return getBytes(randomBytes(32));
}

export { ESCROW_CONTRACT_ADDRESS, DEFAULT_ESCROW_CHAIN_ID };
